import { useEffect, useMemo, useState, useRef } from "react";
import { Button } from "../../tailwind/button";
import { Input } from "../../tailwind/input";
import { Card } from "../../tailwind/card";
import { Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../tailwind/dialog";
import { TemplateSelector } from "../send-message/TemplateSelector";
import { SendTemplateMediaUploader } from "../send-message/SendTemplateMediaUploader";
import { TemplatePreview } from "../templates/TemplatePreview";
import { useSelector } from "react-redux";
import {
  buildTemplateComponentsPayload,
  getTemplateDefinition,
} from "./templateMessageUtils";
import { waBtn } from "../../../pages/whatsapp/utils/whatsappUiTokens";
import { cn } from "../../../lib/utils";

const normalizeInitialValues = (variables = []) =>
  variables.reduce((acc, key) => ({ ...acc, [key]: "" }), {});

export default function ChatTemplateMessageDialog({
  open,
  onOpenChange,
  templates = [],
  onSubmit,
  isSending = false,
  selectedChat,
}) {
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [headerValues, setHeaderValues] = useState({});
  const [bodyValues, setBodyValues] = useState({});
  const [buttonValues, setButtonValues] = useState({});
  const [codeValue, setCodeValue] = useState("");
  const [headerMediaLink, setHeaderMediaLink] = useState("");
  const [selectedMediaFile, setSelectedMediaFile] = useState(null);

  const companyId = useSelector((state) => state.auth?.user?.companyId || state.auth?.companyId);

  const selectedTemplate = useMemo(
    () => templates.find((template) => String(template.id) === String(selectedTemplateId)),
    [templates, selectedTemplateId]
  );

  const templateDefinition = useMemo(() => getTemplateDefinition(selectedTemplate), [selectedTemplate]);

  const isAuthCopyCode = selectedTemplate?.category?.toLowerCase() === 'authentication' && (selectedTemplate?.otpType || 'copy_code') === 'copy_code';

  const buttonVariables = useMemo(() => {
    if (!selectedTemplate?.components?.BUTTONS) return [];
    try {
      const buttonsComponent = typeof selectedTemplate.components.BUTTONS === 'string'
        ? JSON.parse(selectedTemplate.components.BUTTONS)
        : selectedTemplate.components.BUTTONS;
      const buttons = buttonsComponent?.buttons || [];
      const variables = [];
      buttons.forEach((button, index) => {
        if (button.type === 'URL' && button.url) {
          const regex = /\{\{(\d+)\}\}/g;
          const matches = [...button.url.matchAll(regex)];
          matches.forEach(match => {
            variables.push({
              buttonIndex: index + 1,
              paramIndex: parseInt(match[1]),
              key: `button_url_${index + 1}_param_${match[1]}`
            });
          });
        }
      });
      return variables;
    } catch (e) {
      return [];
    }
  }, [selectedTemplate?.components?.BUTTONS]);

  const hasVariables = templateDefinition.headerVariables.length > 0 || templateDefinition.bodyVariables.length > 0 || buttonVariables.length > 0 || isAuthCopyCode;

  useEffect(() => {
    if (!selectedTemplate) {
      setHeaderValues({});
      setBodyValues({});
      setButtonValues({});
      setCodeValue("");
      setHeaderMediaLink("");
      setSelectedMediaFile(null);
      return;
    }
    setHeaderValues(normalizeInitialValues(templateDefinition.headerVariables));
    setBodyValues(normalizeInitialValues(templateDefinition.bodyVariables));
    setButtonValues(normalizeInitialValues(buttonVariables.map(v => v.key)));
    setCodeValue("");
    setHeaderMediaLink("");
    setSelectedMediaFile(null);
  }, [selectedTemplate, templateDefinition.headerVariables, templateDefinition.bodyVariables, buttonVariables]);

  const isFormValid = useMemo(() => {
    if (!selectedTemplate) return false;
    const isHeaderValid = templateDefinition.headerVariables.every((key) => String(headerValues[key] || "").trim());
    const isBodyValid = templateDefinition.bodyVariables.every((key) => String(bodyValues[key] || "").trim());
    const isButtonValid = buttonVariables.every((v) => String(buttonValues[v.key] || "").trim());
    const isCodeValid = !isAuthCopyCode || String(codeValue).trim();
    const isMediaValid = !templateDefinition.needsHeaderMediaLink || Boolean(headerMediaLink.trim()) || Boolean(selectedMediaFile);
    return isHeaderValid && isBodyValid && isButtonValid && isCodeValid && isMediaValid;
  }, [selectedTemplate, templateDefinition, headerValues, bodyValues, buttonValues, codeValue, isAuthCopyCode, headerMediaLink, buttonVariables, selectedMediaFile]);

  const handleMediaChange = (payload) => {
    setSelectedMediaFile(payload);
    setHeaderMediaLink("");
  };

  const handleSend = () => {
    if (!selectedTemplate || !isFormValid) return;

    const components = buildTemplateComponentsPayload({
      template: selectedTemplate,
      headerValues,
      bodyValues,
      buttonValues,
      codeValue,
      isAuthCopyCode,
      headerMediaLink,
      headerMediaId: "",
      buttonVariables,
    });

    const payload = {
      name: selectedTemplate.name,
      language: { code: selectedTemplate.language || "en" },
      components,
    };

    if (selectedMediaFile?.file?.file) {
      payload.mediaFile = selectedMediaFile.file;
      payload.companyId = companyId;
    }

    onSubmit?.(payload);
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl h-[90vh] flex flex-col overflow-hidden bg-gray-50 dark:bg-slate-950 p-0 border-0">
        <DialogHeader className="p-6 pb-4 shrink-0 border-b border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <DialogTitle className="text-xl">Send Template Message</DialogTitle>
          <DialogDescription>Select an approved template and fill required values to send a message.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto grid grid-cols-1 lg:grid-cols-5">
          <div className="lg:col-span-3 p-6 space-y-6">
            <TemplateSelector
              templates={templates}
              selectedId={selectedTemplateId}
              onSelect={setSelectedTemplateId}
              hideStepNumber={true}
            />

          {templateDefinition.needsHeaderMediaLink && (
            <div className="space-y-2">
              <SendTemplateMediaUploader
                mode="chat"
                headerType={templateDefinition.headerFormat}
                disabled={isSending}
                onFileChange={handleMediaChange}
                selectedFile={selectedMediaFile}
              />
            </div>
          )}

          {hasVariables && (
            <Card className="p-6 border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Template Variables</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templateDefinition.headerVariables.map((key) => (
                  <div key={`header-${key}`}>
                    <label className="text-gray-600 dark:text-slate-400 text-sm mb-1.5 block">
                      Header Variable: {key} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-md text-sm focus:ring-2 focus:ring-[hsl(158, 48%, 41%)] text-gray-900 dark:text-white outline-none"
                      value={headerValues[key] || ""}
                      onChange={(event) => setHeaderValues((prev) => ({ ...prev, [key]: event.target.value }))}
                    />
                  </div>
                ))}
                {templateDefinition.bodyVariables.map((key) => (
                  <div key={`body-${key}`}>
                    <label className="text-gray-600 dark:text-slate-400 text-sm mb-1.5 block">
                      Body Variable: {key} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-md text-sm focus:ring-2 focus:ring-[hsl(158, 48%, 41%)] text-gray-900 dark:text-white outline-none"
                      value={bodyValues[key] || ""}
                      onChange={(event) => setBodyValues((prev) => ({ ...prev, [key]: event.target.value }))}
                    />
                  </div>
                ))}
                {buttonVariables.map((variable) => (
                  <div key={`button-${variable.key}`}>
                    <label className="text-gray-600 dark:text-slate-400 text-sm mb-1.5 block">
                      Button {variable.buttonIndex} URL Variable: {`{${variable.paramIndex}}`} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-md text-sm focus:ring-2 focus:ring-[hsl(158, 48%, 41%)] text-gray-900 dark:text-white outline-none"
                      value={buttonValues[variable.key] || ""}
                      onChange={(event) => setButtonValues((prev) => ({ ...prev, [variable.key]: event.target.value }))}
                    />
                  </div>
                ))}
                {isAuthCopyCode && (
                  <div key="auth-code">
                    <label className="text-gray-600 dark:text-slate-400 text-sm mb-1.5 block">
                      Authentication Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-md text-sm focus:ring-2 focus:ring-[hsl(158, 48%, 41%)] text-gray-900 dark:text-white outline-none"
                      value={codeValue || ""}
                      onChange={(event) => setCodeValue(event.target.value)}
                    />
                  </div>
                )}
              </div>
            </Card>
          )}

          {selectedTemplateId && selectedTemplate?.status && selectedTemplate.status.toLowerCase() !== 'approved' && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-400">
              ⚠️ Cannot send messages. Your template is currently {selectedTemplate.status.toLowerCase()}. Templates must be approved before sending messages.
            </div>
          )}
        </div>

        <div className="lg:col-span-2 bg-slate-100 dark:bg-slate-900 border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-slate-800 p-6">
          <TemplatePreview data={{
            headerType: templateDefinition.headerFormat || 'none',
            headerText: selectedTemplate?.headerText || '',
            bodyText: selectedTemplate?.body || '',
            footerText: selectedTemplate?.footerText || '',
            buttons: selectedTemplate?.buttons || [],
            sampleValues: { ...headerValues, ...bodyValues, ...buttonValues, ...(isAuthCopyCode ? { code: codeValue } : {}) },
            mediaType: headerMediaLink || selectedMediaFile ? templateDefinition.headerFormat : 'none',
            mediaPreview: selectedMediaFile?.preview || headerMediaLink || '',
            components: selectedTemplate?.components || null,
            isAuthTemplate: isAuthCopyCode || String(selectedTemplate?.category || '').toLowerCase() === 'authentication',
            otpType: selectedTemplate?.otpType || 'copy_code',
            otpButtonText: selectedTemplate?.otpButtonText || '',
            codeExpirationMinutes: selectedTemplate?.codeExpirationMinutes || 10,
            addSecurityRecommendation: !!selectedTemplate?.addSecurityRecommendation,
          }}
          mode="send" />
        </div>
      </div>

        <DialogFooter className="p-6 pt-2 shrink-0 border-t border-gray-200 dark:border-slate-800 mt-auto bg-white dark:bg-slate-900">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} className="h-12 px-6">
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={!isFormValid || isSending || !selectedTemplateId || (selectedTemplate?.status && selectedTemplate.status.toLowerCase() !== 'approved')}
            className={cn("h-12 px-6 shadow-[hsl(158, 48%, 41%)]/20", waBtn.primary)}
          >
            {isSending ? "Sending..." : (
              <span className="flex items-center gap-2">
                <Send className="h-4 w-4" />
                Send Template
              </span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

